/**
 * MemberSecurityPage — sign-in methods panel.
 *
 * Shown at /members/security. Answers "how can I sign in to this account,
 * and how do I add another way?" (Oracle v1 design rule applied.)
 *
 * Sections:
 *   • Password — status + link to change/set
 *   • Linked providers — list, link Google, unlink (guarded)
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Link2, Unlink } from "lucide-react";
import { socialApi, startGoogleAuth, formatApiError } from "../../lib/authApi";
import { useMemberAuth } from "../../hooks/useMemberAuth";

const PROVIDER_LABELS = {
  google: "Google",
  magic_link: "Magic link (email)",
  microsoft: "Microsoft",
  apple: "Apple",
  github: "GitHub",
};

const MemberSecurityPage = () => {
  const nav = useNavigate();
  const { member, loading } = useMemberAuth();
  const [state, setState] = useState({ password_set: false, linked: [], available: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await socialApi.listProviders();
      setState(data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Could not load sign-in methods.");
    }
  }, []);

  useEffect(() => {
    if (!loading && !member) {
      nav("/auth/login?next=/members/security");
      return;
    }
    if (member) load();
  }, [loading, member, load, nav]);

  const onLinkGoogle = () => startGoogleAuth({ link: true });

  const onUnlink = async (provider) => {
    if (!confirm(`Remove ${PROVIDER_LABELS[provider] || provider} from this account?`)) return;
    setBusy(true);
    try {
      await socialApi.unlinkProvider(provider);
      toast.success(`${PROVIDER_LABELS[provider] || provider} unlinked.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Unlink failed.");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !member) {
    return (
      <div className="max-w-2xl mx-auto py-10 px-4 font-mono text-[11px] uppercase tracking-[0.3em] text-[#A39E93]">
        // LOADING SECURITY PROFILE…
      </div>
    );
  }

  const linkedGoogle = state.linked.some((p) => p.provider === "google");
  const totalMethods = state.linked.length + (state.password_set ? 1 : 0);

  return (
    <div className="max-w-2xl mx-auto py-10 px-4" data-testid="member-security-page">
      <Link
        to="/members"
        data-testid="member-security-back"
        className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20] mb-6"
      >
        <ArrowLeft size={12} /> Back to Member Desk
      </Link>

      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#D92D20]">
          // SIGN-IN METHODS
        </div>
        <h1 className="font-display text-2xl sm:text-3xl mt-2 text-[#E4DFD3] leading-tight">
          Access control
        </h1>
        <p className="text-sm mt-2 text-[#A39E93] leading-relaxed">
          Configure how you sign in to this account. You must keep at least one
          method active — otherwise you would be locked out.
        </p>
      </div>

      {error && (
        <div
          data-testid="member-security-error"
          className="font-mono text-[11px] text-[#D92D20] border border-[#D92D20]/40 p-3 mb-6"
        >
          {error}
        </div>
      )}

      {/* Password panel */}
      <div className="border p-5 mb-5" style={{ borderColor: "#2D2A26", background: "#0F0E12" }}
           data-testid="password-panel">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] flex items-center gap-2">
              <ShieldCheck size={12} /> Password
            </div>
            <div className="text-[15px] text-[#E4DFD3] mt-1">
              {state.password_set ? "Enabled" : "Not set"}
            </div>
            <div className="text-[11px] font-mono text-[#6F6A60] mt-1">
              {state.password_set
                ? "Sign in with email and password."
                : "You can set a password to sign in without a magic link or Google."}
            </div>
          </div>
          {state.password_set ? (
            <Link
              to="/members/change-password"
              className="font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 border text-[#A39E93] hover:border-[#D92D20] hover:text-[#D92D20]"
              style={{ borderColor: "#454038" }}
              data-testid="password-change-link"
            >
              Change
            </Link>
          ) : (
            <Link
              to="/auth/forgot-password"
              className="font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 border text-[#D92D20]"
              style={{ borderColor: "#D92D20" }}
              data-testid="password-set-link"
            >
              Set password
            </Link>
          )}
        </div>
      </div>

      {/* Linked providers */}
      <div className="border p-5" style={{ borderColor: "#2D2A26", background: "#0F0E12" }}
           data-testid="providers-panel">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] flex items-center gap-2 mb-3">
          <Link2 size={12} /> Linked providers
        </div>

        {state.linked.length === 0 ? (
          <div className="text-[12px] text-[#6F6A60] font-mono mb-4">
            No providers linked yet.
          </div>
        ) : (
          <ul className="mb-4">
            {state.linked.map((p) => (
              <li
                key={p.provider}
                data-testid={`linked-provider-${p.provider}`}
                className="flex items-center justify-between py-2 border-b last:border-b-0"
                style={{ borderColor: "#2D2A26" }}
              >
                <div>
                  <div className="text-[13px] text-[#E4DFD3]">
                    {PROVIDER_LABELS[p.provider] || p.provider}
                  </div>
                  <div className="text-[10px] font-mono text-[#6F6A60]">{p.email}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onUnlink(p.provider)}
                  disabled={busy || totalMethods <= 1}
                  data-testid={`unlink-${p.provider}`}
                  title={
                    totalMethods <= 1
                      ? "You must have at least one sign-in method. Set a password first."
                      : `Remove ${PROVIDER_LABELS[p.provider] || p.provider}`
                  }
                  className="flex items-center gap-1 text-[10px] uppercase font-mono tracking-[0.25em] text-[#A39E93] hover:text-[#D92D20] disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Unlink size={10} /> Unlink
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Actions to add more */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {!linkedGoogle && (
            <button
              type="button"
              onClick={onLinkGoogle}
              data-testid="link-google"
              className="flex items-center justify-center gap-2 px-3 py-2.5 border font-mono text-[11px] uppercase tracking-[0.25em] text-[#E4DFD3] hover:border-[#D92D20]"
              style={{ borderColor: "#454038", background: "#08070A" }}
            >
              <Link2 size={12} /> Link Google
            </button>
          )}
        </div>

        {/* Available-soon note lists providers we know about but haven't shipped */}
        {state.available.length > state.linked.length + (linkedGoogle ? 0 : 1) && (
          <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.25em] text-[#6F6A60]">
            More providers coming soon (Microsoft, Apple, GitHub).
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberSecurityPage;
